import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Tooltip from '../Tooltip';
import ConnectionInfo from './ConnectionInfo';
import api from '../../API';
import styles from './GirderConnection.module.css';
import SyncWorkspace from './sync/SyncWorkspace';
import SyncHistoryModal from './sync/SyncHistoryModal';
import RenumberNodeModal from './RenumberNodeModal';
import { loadSyncMode, saveSyncMode } from './sync/syncStatusUtils';
import { selectAllUuids, collectDiffEntries } from './sync/diffSelectorUtils';
import { useNetworkData } from '../../hooks/network/useNetworkData';

/**
 * Get status badge CSS class for a connection status
 */
function getStatusBadgeClass(status, styles) {
    const statusMap = {
        'initiated': styles.statusBadgeInitiated,
        'unverified': styles.statusBadgeUnverified,
        'awaiting-approval': styles.statusBadgeAwaitingApproval,
        'connecting': styles.statusBadgeConnecting,
        'connected': styles.statusBadgeConnected,
        'canceled': styles.statusBadgeCanceled,
        'rejected': styles.statusBadgeRejected,
        'terminated': styles.statusBadgeTerminated,
        'unexpected': styles.statusBadgeNeedsAttention,
        'failed': styles.statusBadgeNeedsAttention,
    };
    return statusMap[status] || '';
}

function statusBadgeLabel(status) {
    if (status === 'unexpected' || status === 'failed') {
        return 'Needs attention';
    }
    return status;
}

function connectionStatusHelper(connection) {
    if (connection.status === 'unexpected') {
        const reported = connection['unexpected-status'];
        if (reported) {
            return `Peer reported status “${reported}”. Terminate or delete this request, then reconnect if needed.`;
        }
        const hist = connection.statusHistory;
        if (Array.isArray(hist) && hist.length) {
            const last = hist[hist.length - 1];
            if (last?.message) return last.message;
        }
        return 'This connection is in a bad state. Terminate or delete it, then reconnect if needed.';
    }
    if (connection.status === 'failed') {
        const hist = connection.statusHistory;
        if (Array.isArray(hist) && hist.length) {
            const last = hist[hist.length - 1];
            if (last?.message) return last.message;
        }
        return 'This connection is in a bad state. Terminate or delete it, then reconnect if needed.';
    }
    return null;
}

const SWITCH_TOOLTIP_PROXIED =
    'Load data using the hub as a proxy (useful if a firewall blocks a direct connection, for example)';
const SWITCH_TOOLTIP_DIRECT =
    'Load data directly from the source (more efficient, recommended, if firewalls allow the connection)';

/**
 * GirderConnection component displays a single connection with its details and actions
 */
export default function GirderConnection({
    connection,
    networkNode = null,
    mode,
    role,
    connectedList,
    mutations,
    isChild = false,
    childConnections = []
}) {
    const {
        cancelConnection,
        deleteConnection,
        rejectConnection,
        approveConnection,
        syncRemote,
        fetchSyncDiff,
        updateAutoPull,
        terminateConnection,
        switchConnectionType,
    } = mutations;

    const queryClient = useQueryClient();
    const { local: localSite } = useNetworkData();
    
    // Sync functionality for connected connections
    const connectionId = connection._id;
    const folderId = connection.folderId; // Still needed for folder links
    const isConnected = connection.status === 'connected';
    
    const [syncMode, setSyncMode] = useState(() => loadSyncMode(connectionId));
    const [syncError, setSyncError] = useState(null);
    const [diffReview, setDiffReview] = useState(null);
    const [selectedUuids, setSelectedUuids] = useState([]);
    const [terminateRemoteWarning, setTerminateRemoteWarning] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [childHistory, setChildHistory] = useState(null);
    const [renumberOpen, setRenumberOpen] = useState(false);
    
    const handleSyncModeChange = (mode) => {
        setSyncMode(mode);
        saveSyncMode(connectionId, mode);
        setDiffReview(null);
        setSelectedUuids([]);
    };

    const handleSelectionChange = useCallback((selectionSet) => {
        setSelectedUuids(Array.from(selectionSet));
    }, []);
    
    // Get latest sync record for connected connections
    const { data: syncRecord } = useQuery({
        queryKey: ['sync-record', connectionId],
        queryFn: () => {
            console.log('[Frontend Sync] Fetching sync record', { connectionId });
            return api.get('network/sync/record/latest', {query: {connectionId}});
        },
        enabled: !!connectionId && isConnected,
        refetchInterval: (query) => {
            const status = query.state.data?.data?.status;
            // Stop polling if status is 'completed', 'error', or 'never_synced'
            if (status === 'completed' || status === 'error' || status === 'never_synced') {
                return false;
            }
            // Poll every 2 seconds if in progress or pending
            return (status === 'in_progress' || status === 'pending') ? 2000 : false;
        },
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            console.log('[Frontend Sync] Sync record received', { 
                connectionId,
                status: data?.data?.status 
            });
        }
    });
    
    // Log sync record changes
    useEffect(() => {
        if (syncRecord?.data && isConnected) {
            console.log('[Frontend Sync] Sync record changed', {
                connectionId,
                status: syncRecord.data.status,
                statistics: syncRecord.data.statistics,
                pluginData: syncRecord.data.pluginData,
                completedAt: syncRecord.data.completedAt,
                startedAt: syncRecord.data.startedAt
            });
        }
    }, [syncRecord?.data, connectionId, isConnected]);
    
    const runSync = (selected = null) => {
        setSyncError(null);
        setDiffReview(null);
        setSelectedUuids([]);
        syncRemote.mutate(
            { connectionId: connection._id, selectedUuids: selected },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries(['sync-record', connectionId]);
                },
                onError: (error) => {
                    const errorMessage = error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'An unknown error occurred during sync';
                    setSyncError(errorMessage);
                },
            }
        );
    };

    const handleFullSyncClick = () => {
        if (connection._id) runSync(null);
    };

    const handleCheckForChanges = () => {
        setSyncError(null);
        fetchSyncDiff.mutate(
            { connectionId: connection._id },
            {
                onSuccess: (data) => {
                    const payload = data?.data;
                    if (!payload?.diffResult) return;
                    setDiffReview({
                        diffResult: payload.diffResult,
                        statistics: payload.statistics,
                        pluginPreview: payload.pluginPreview,
                    });
                    const all = new Set();
                    for (const nodeDiff of payload.diffResult.diffs || []) {
                        const { entries } = collectDiffEntries(nodeDiff);
                        for (const uuid of selectAllUuids(entries)) all.add(uuid);
                    }
                    setSelectedUuids(Array.from(all));
                },
                onError: (error) => {
                    setDiffReview(null);
                    setSelectedUuids([]);
                    const errorMessage = error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Failed to fetch sync diff';
                    setSyncError(errorMessage);
                },
            }
        );
    };

    const handleSyncSelected = () => {
        if (selectedUuids.length > 0) runSync(selectedUuids);
    };

    const handleDiscardDiff = () => {
        setDiffReview(null);
        setSelectedUuids([]);
    };
    
    const handleDismissError = () => {
        setSyncError(null);
    };
    
    // Check if sync is stuck (in_progress for more than 5 minutes)
    const syncRecordData = syncRecord?.data;
    const isStuck = syncRecordData?.status === 'in_progress' && syncRecordData?.startedAt && 
        (Date.now() - new Date(syncRecordData.startedAt).getTime()) > 5 * 60 * 1000;
    
    // Check if sync is in progress
    const isSyncInProgress = syncRecordData?.status === 'in_progress' || syncRecordData?.status === 'pending';
    
    // Reset sync status mutation
    const resetSyncStatus = useMutation({
        mutationFn: (connectionId) => api.put('network/sync/reset', {query: {connectionId}}),
        onSuccess: () => {
            // Invalidate the sync record query to stop polling and refresh
            queryClient.invalidateQueries({ queryKey: ['sync-record', connectionId] });
        },
        onError: (error) => {
            console.error('Error resetting sync status:', error);
        }
    });

    // Check if this pending connection would conflict with an existing connected connection
    const conflictingRemote = connectedList.find(conn => conn.url === connection.url);
    const isApproving = approveConnection.isPending;
    
    // Check if this connection has an approve error
    // Note: We check if the error is related to this connection by checking the URL in the error
    const approveError = approveConnection.error;
    const errorResponse = approveError?.response?.data || approveError?.data;
    const errorApiUrl = errorResponse?.duplicateApiUrl;
    
    // Check if error is related to this connection by:
    // 1. duplicateApiUrl matches connection.url (for duplicate errors)
    // 2. Error message contains connection URL (for network errors like offline)
    const errorMessage = errorResponse?.error || errorResponse?.message || '';
    const connectionUrl = connection.url || '';
    
    // Extract base URL from connection URL (remove API paths)
    const getBaseUrl = (url) => {
        if (!url) return '';
        // Remove common API path patterns and trailing slashes
        let baseUrl = url.replace(/\/dsa\/api\/v1.*$/, '').replace(/\/bdsa\/api\/v1.*$/, '').replace(/\/api\/v1.*$/, '').replace(/\/$/, '');
        // Also handle host.docker.internal vs localhost
        baseUrl = baseUrl.replace(/host\.docker\.internal/g, 'localhost');
        return baseUrl;
    };
    
    const connectionBaseUrl = getBaseUrl(connectionUrl);
    // Normalize error message for comparison (convert host.docker.internal to localhost)
    const normalizedErrorMessage = errorMessage.replace(/host\.docker\.internal/g, 'localhost');
    // Check if error message contains the connection's base URL
    const errorContainsConnectionUrl = errorMessage && connectionBaseUrl && 
        normalizedErrorMessage.includes(connectionBaseUrl);
    
    // Match error to connection if:
    // - duplicateApiUrl matches (for duplicate errors), OR
    // - Connection is pending and error message contains the connection's URL (for network errors)
    const errorMatchesThisConnection = (errorApiUrl && errorApiUrl === connection.url) || 
                                       (approveError && 
                                        (connection.status === 'unverified' || connection.status === 'awaiting-approval') && 
                                        errorContainsConnectionUrl);
    const duplicateError = errorApiUrl && errorApiUrl === connection.url && errorResponse?.error && errorResponse?.error.includes('already exists');
    const existingConnection = errorApiUrl && errorApiUrl === connection.url ? errorResponse?.existingConnection : null;
    
    // Check for assetstore conflict
    const conflictingAssetstore = errorResponse?.conflictingAssetstore;
    const connectionUuid = errorResponse?.connectionUuid;
    const assetstoreConflict = conflictingAssetstore && connectionUuid && connectionUuid === connection.connectionUuid;

    // If this is a child connection, render simplified view
    if (isChild) {
        return (
            <div key={connection._id} className={`${styles.connection} ${styles.connectionChild}`} data-uuid={connection.connectionUuid} data-sourcepanel="GirderConnection.js">
                <div className={styles.connectionHeader}>
                    <div className={styles.connectionStatus}>
                        <span
                            className={`${styles.statusBadge} ${getStatusBadgeClass(connection.status, styles)}`}
                            title={connection.status}
                        >
                            {statusBadgeLabel(connection.status)}
                        </span>
                    </div>
                </div>
                <div className={styles.connectionContent}>
                    <div className={styles.connectionInfo}>
                        <div className={styles.childConnectionName}>
                            {connection.remoteDisplayName || 'Unknown'}
                        </div>
                        {connectionStatusHelper(connection) && (
                            <div className={styles.statusHelperText}>
                                {connectionStatusHelper(connection)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Determine connection type for display
    const isDirectConnection = connection.role === 'mediated-initiator' || connection.role === 'mediated-responder' || 
                               connection.role === 'initiator' || connection.role === 'responder';
    const isProxiedConnection = !isDirectConnection && connection.status === 'connected';
    
    return (
        <div key={connection._id} className={styles.connection} data-uuid={connection.connectionUuid} data-sourcepanel="GirderConnection.js">
            <div className={styles.connectionHeader}>
                <div className={styles.connectionStatus}>
                    <span
                        className={`${styles.statusBadge} ${getStatusBadgeClass(connection.status, styles)}`}
                        title={connection.status}
                    >
                        {statusBadgeLabel(connection.status)}
                    </span>
                    {connection.status === 'connected' && (
                        <span className={styles.connectionTypeBadge}>
                            {isDirectConnection ? 'Direct' : isProxiedConnection ? 'Proxied' : ''}
                        </span>
                    )}
                    {connection.status === 'connected'
                        && !(connection.role || '').startsWith('mediated')
                        && !connection.parentConnectionId && (
                        <span
                            className={`${styles.autoSyncHeaderBadge} ${
                                connection.autoPull?.enabled
                                    ? styles.autoSyncHeaderBadgeOn
                                    : styles.autoSyncHeaderBadgeOff
                            }`}
                            title={
                                connection.autoPull?.enabled
                                    ? 'Auto-sync is enabled for this connection'
                                    : 'Auto-sync is disabled for this connection'
                            }
                        >
                            {connection.autoPull?.enabled ? 'Auto-sync on' : 'Auto-sync off'}
                        </span>
                    )}
                </div>
                {connection.status === 'initiated' ? (
                    <Tooltip text="Cancel" position="top">
                        <button 
                            className={styles.cancelButtonIcon}
                            onClick={() => cancelConnection.mutate(connection._id)}
                        >
                            ✕
                        </button>
                    </Tooltip>
                ) : connection.status === 'connected' ? (
                    <div className={styles.actionButtonsContainer}>
                        <button 
                            className={styles.terminateButton}
                            onClick={() => {
                                setTerminateRemoteWarning(null);
                                terminateConnection.mutate(connection._id, {
                                    onSuccess: (res) => {
                                        const notify = res?.data?.remoteNotify;
                                        if (notify && notify !== 'ok' && notify !== 'skipped') {
                                            setTerminateRemoteWarning(
                                                `This node ended the connection, but the peer was not notified successfully (${notify}). Ask the administrator of the remote node to confirm their side is cleaned up (terminate or delete any leftover connection) so it does not stay in an Unexpected or Connected state.`
                                            );
                                        }
                                    },
                                });
                            }}
                            disabled={
                                terminateConnection.isPending
                                && terminateConnection.variables === connection._id
                            }
                        >
                            {terminateConnection.isPending
                                && terminateConnection.variables === connection._id
                                ? 'Terminating...'
                                : 'Terminate connection'}
                        </button>
                        {terminateConnection.isError
                            && terminateConnection.variables === connection._id && (
                            <div className={styles.inlineActionError}>
                                {terminateConnection.error?.message}
                            </div>
                        )}
                        {terminateRemoteWarning && (
                            <div className={styles.inlineActionWarning}>
                                {terminateRemoteWarning}
                            </div>
                        )}
                    </div>
                ) : (connection.status === 'canceled' || connection.status === 'rejected' || connection.status === 'terminated' || connection.status === 'unexpected' || connection.status === 'failed') ? (
                    <div className={styles.actionButtonsContainer}>
                        <button 
                            className={styles.deleteButton}
                            onClick={() => deleteConnection.mutate(connection._id)}
                            disabled={
                                deleteConnection.isPending
                                && deleteConnection.variables === connection._id
                            }
                            title={connection.status === 'terminated' ? "Clear from list" : "Delete connection"}
                        >
                            <span className={styles.buttonIcon}>🗑️</span>
                            <span className={styles.buttonText}>
                                {deleteConnection.isPending
                                    && deleteConnection.variables === connection._id
                                    ? 'Deleting...'
                                    : 'Delete'}
                            </span>
                        </button>
                        {deleteConnection.isError
                            && deleteConnection.variables === connection._id && (
                            <div className={styles.inlineActionError}>
                                {deleteConnection.error?.message}
                            </div>
                        )}
                    </div>
                ) : (connection.status === 'unverified' || connection.status === 'awaiting-approval') ? (
                    <div className={styles.actionButtonsContainer}>
                        <button 
                            className={styles.approveButton}
                            onClick={() => {
                                // Reset error state by clearing any previous errors
                                approveConnection.reset();
                                approveConnection.mutate({id: connection._id});
                            }}
                            disabled={conflictingRemote || assetstoreConflict || isApproving}
                            title={conflictingRemote || assetstoreConflict ? "Cannot approve: conflict exists" : (isApproving ? "Approving..." : "Approve")}
                        >
                            <span className={styles.buttonIcon}>✓</span>
                            <span className={styles.buttonText}>
                                {isApproving ? 'Approving...' : 'Approve'}
                            </span>
                        </button>
                        <button 
                            className={styles.rejectButton}
                            onClick={() => rejectConnection.mutate(connection._id)}
                            disabled={isApproving}
                            title="Reject"
                        >
                            <span className={styles.buttonIcon}>✕</span>
                            <span className={styles.buttonText}>Reject</span>
                        </button>
                    </div>
                ) : null}
            </div>
            <div className={styles.connectionContent}>
                <div className={styles.connectionInfo}>
                    <ConnectionInfo
                        connection={connection}
                        mode={mode}
                        role={role}
                        folderId={isConnected ? folderId : undefined}
                        conflictingRemote={conflictingRemote}
                        approveError={approveError}
                        errorResponse={errorResponse}
                        errorMatchesThisConnection={errorMatchesThisConnection}
                        duplicateError={duplicateError}
                        existingConnection={existingConnection}
                        assetstoreConflict={assetstoreConflict}
                        conflictingAssetstore={conflictingAssetstore}
                        isApprovingWithAssetstore={isApproving}
                        approveConnection={approveConnection}
                    />
                    {connectionStatusHelper(connection) && (
                        <div className={styles.statusHelperText}>
                            {connectionStatusHelper(connection)}
                        </div>
                    )}
                    {isConnected && (
                        <>
                            {role === 'hub' && networkNode?.numericId != null && (
                                <div style={{ marginBottom: '0.75em' }}>
                                    <button
                                        type="button"
                                        className={styles.syncButtonSecondary}
                                        onClick={() => setRenumberOpen(true)}
                                    >
                                        Change node ID…
                                        {networkNode.networkIdentifier
                                            ? ` (${networkNode.networkIdentifier})`
                                            : ''}
                                    </button>
                                    <RenumberNodeModal
                                        open={renumberOpen}
                                        onClose={() => setRenumberOpen(false)}
                                        connectionId={connectionId}
                                        currentNumericId={networkNode.numericId}
                                        currentIdentifier={networkNode.networkIdentifier}
                                        networkName={localSite?.networkName}
                                        peerName={connection.remoteDisplayName}
                                    />
                                </div>
                            )}
                            <SyncWorkspace
                                showAutoSync={
                                    !(connection.role || '').startsWith('mediated')
                                    && !connection.parentConnectionId
                                }
                                connection={connection}
                                updateAutoPull={updateAutoPull}
                                syncMode={syncMode}
                                onSyncModeChange={handleSyncModeChange}
                                isSyncInProgress={isSyncInProgress}
                                connectionId={connectionId}
                                folderId={folderId}
                                onFullSync={handleFullSyncClick}
                                onCheckForChanges={handleCheckForChanges}
                                onSyncSelected={handleSyncSelected}
                                onResetSync={() => resetSyncStatus.mutate(connectionId)}
                                syncRemotePending={syncRemote.isPending}
                                fetchDiffPending={fetchSyncDiff.isPending}
                                resetSyncPending={resetSyncStatus.isPending}
                                selectedCount={selectedUuids.length}
                                syncRecordStatus={syncRecordData?.status}
                                isStuck={isStuck}
                                remoteName={connection.remoteDisplayName}
                                syncRecord={syncRecordData}
                                diffReview={diffReview}
                                syncError={syncError}
                                onDismissError={handleDismissError}
                                onDiscardDiff={handleDiscardDiff}
                                selectedUuids={selectedUuids}
                                onSelectionChange={handleSelectionChange}
                                onOpenHistory={() => setHistoryOpen(true)}
                            />
                            <SyncHistoryModal
                                open={historyOpen}
                                onClose={() => setHistoryOpen(false)}
                                connectionId={connectionId}
                                nodeUuid={connection.girderNetworkUuid || null}
                                peerName={connection.remoteDisplayName}
                            />
                        </>
                    )}
                    {updateAutoPull?.isError
                        && updateAutoPull.variables?.connectionId === connection._id && (
                        <div className={styles.inlineActionError}>
                            {updateAutoPull.error?.message}
                        </div>
                    )}
                </div>
                <div className={styles.connectionActions}>
                    {/* Action buttons are now in the top right corner */}
                </div>
            </div>
            {/* Render child connections inside the parent panel */}
            {childConnections.length > 0 && (
                <div className={styles.childrenContainer}>
                    <p className={styles.childrenLegend}>
                        Outbound: our link to this peer. Inbound: their link to us. Switch to Proxied/Direct changes how we load their data (outbound).
                    </p>
                    {childConnections.map(childGroup => {
                        // Backend returns unified structure with:
                        // - girderNetworkUuid
                        // - outboundConnection (mediated-initiator connection or null)
                        // - inboundConnection (mediated-responder connection or null)
                        // - networkNode (remote NetworkNode if connected, or null)
                        // - proxiedNode (proxied NetworkNode if proxied, or null)
                        
                        const { 
                            girderNetworkUuid, 
                            outboundConnection, 
                            inboundConnection, 
                            networkNode, 
                            proxiedNode 
                        } = childGroup;
                        
                        // Get display name from any available source
                        const displayName = outboundConnection?.remoteDisplayName || 
                                           inboundConnection?.remoteDisplayName || 
                                           networkNode?.displayName ||
                                           proxiedNode?.displayName || 
                                           'Unknown';
                        
                        // Determine connection status for each direction:
                        // Outbound: Direct if outboundConnection exists and is connected, else Proxied
                        const outboundIsDirect = outboundConnection && outboundConnection.status === 'connected';
                        const outboundIsProxied = !outboundIsDirect; // If no direct connection, it's proxied
                        
                        // Inbound: Direct if inboundConnection exists and is connected, else Proxied
                        // Per user requirement: "if we do not have a connected mediated-responder connection 
                        // that the connection from the other node is proxied"
                        const inboundIsDirect = inboundConnection && inboundConnection.status === 'connected';
                        const inboundIsProxied = !inboundIsDirect; // If no connected inbound, it's proxied
                        
                        // Use outbound connection for toggle (we control this direction)
                        // Can only toggle if we have a direct outbound connection that's connected
                        const connectionForToggle = outboundConnection;
                        const canToggle = outboundIsDirect; // Only if outbound is direct and connected
                        const switchPendingForPeer =
                            switchConnectionType.isPending
                            && switchConnectionType.variables?.girderNetworkUuid === girderNetworkUuid;
                        const switchErrorForPeer =
                            switchConnectionType.isError
                            && switchConnectionType.variables?.girderNetworkUuid === girderNetworkUuid;
                        
                        return (
                            <div key={girderNetworkUuid} className={styles.childConnectionGroup}>
                                <div className={styles.childConnectionHeader}>
                                    <span className={styles.childConnectionName}>
                                        {displayName}
                                    </span>
                                    {isConnected && connectionId && (
                                        <button
                                            type="button"
                                            className={styles.syncHistoryLink}
                                            onClick={() => setChildHistory({
                                                nodeUuid: girderNetworkUuid,
                                                peerName: displayName,
                                            })}
                                        >
                                            History
                                        </button>
                                    )}
                                </div>
                                <div className={styles.childConnectionDetails}>
                                    {/* Outbound connection status */}
                                    <div className={styles.connectionDirection}>
                                        <span className={styles.directionLabel}>Outbound:</span>
                                        {outboundIsDirect ? (
                                            <>
                                                <span className={`${styles.statusBadge} ${getStatusBadgeClass(outboundConnection.status, styles)}`}>
                                                    {outboundConnection.status}
                                                </span>
                                                <span className={styles.connectionTypeBadge}>Direct</span>
                                            </>
                                        ) : (
                                            <span className={styles.connectionTypeBadge}>Proxied</span>
                                        )}
                                    </div>
                                    
                                    {/* Inbound connection status */}
                                    <div className={styles.connectionDirection}>
                                        <span className={styles.directionLabel}>Inbound:</span>
                                        {inboundIsDirect ? (
                                            <>
                                                <span className={`${styles.statusBadge} ${getStatusBadgeClass(inboundConnection.status, styles)}`}>
                                                    {inboundConnection.status}
                                                </span>
                                                <span className={styles.connectionTypeBadge}>Direct</span>
                                            </>
                                        ) : (
                                            <span className={styles.connectionTypeBadge}>Proxied</span>
                                        )}
                                    </div>
                                    
                                    {/* Toggle button - only on outbound connection when it's direct and connected */}
                                    {canToggle && (
                                        <div className={styles.childConnectionActions}>
                                            <button 
                                                className={styles.connectionTypeButton}
                                                title={SWITCH_TOOLTIP_PROXIED}
                                                onClick={() => {
                                                    switchConnectionType.reset();
                                                    switchConnectionType.mutate({
                                                        girderNetworkUuid: girderNetworkUuid,
                                                        connectionType: 'proxied'
                                                    });
                                                }}
                                                disabled={switchPendingForPeer}
                                            >
                                                {switchPendingForPeer ? 'Switching...' : 'Switch to Proxied'}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Button to switch from proxied to direct */}
                                    {outboundIsProxied && proxiedNode && (
                                        <div className={styles.childConnectionActions}>
                                            <button 
                                                className={styles.connectionTypeButton}
                                                title={SWITCH_TOOLTIP_DIRECT}
                                                onClick={() => {
                                                    switchConnectionType.reset();
                                                    switchConnectionType.mutate({
                                                        girderNetworkUuid: girderNetworkUuid,
                                                        connectionType: 'direct'
                                                    });
                                                }}
                                                disabled={switchPendingForPeer}
                                            >
                                                {switchPendingForPeer ? 'Switching...' : 'Switch to Direct'}
                                            </button>
                                        </div>
                                    )}
                                    {switchErrorForPeer && (
                                        <div className={styles.inlineActionError}>
                                            {switchConnectionType.error?.message}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <SyncHistoryModal
                open={!!childHistory}
                onClose={() => setChildHistory(null)}
                connectionId={connectionId}
                nodeUuid={childHistory?.nodeUuid || null}
                peerName={childHistory?.peerName || null}
            />
        </div>
    );
}

